import { useEffect, useState } from 'react';
import { getAdminAttensi, saveAttensiCourse, GetAdminAttensiOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Circle, Loader2, Pencil } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Course = GetAdminAttensiOutputType['courses'][0];
type Employee = GetAdminAttensiOutputType['employees'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const blank = { courseName: '', employeeId: '', status: 'Required', completedDate: '', attensiUrl: '' };

export default function AttensiAdminTab() {
  const [data, setData] = useState<GetAdminAttensiOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...blank });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    getAdminAttensi({}).then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...blank }); setDialogOpen(true); };

  const openEdit = (c: Course) => {
    setEditId(c.id);
    setForm({
      courseName: c.courseName,
      employeeId: c.employeeId || '',
      status: c.status,
      completedDate: c.completedDate || '',
      attensiUrl: c.attensiUrl || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.courseName.trim() || !form.employeeId) { toast.error('Course name and employee are required'); return; }
    setSaving(true);
    try {
      await saveAttensiCourse({
        id: editId || undefined,
        courseName: form.courseName,
        employeeId: form.employeeId,
        status: form.status,
        completedDate: form.completedDate || undefined,
        attensiUrl: form.attensiUrl || undefined,
      });
      toast.success(editId ? 'Updated' : 'Course assigned');
      setDialogOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const filtered = data?.courses.filter(c =>
    c.courseName.toLowerCase().includes(search.toLowerCase()) ||
    c.employeeName.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <Input placeholder="Search courses or employees..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
        <Button onClick={openNew} size="sm" className="bg-primary text-primary-foreground shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Assign Course
        </Button>
      </div>

      <div className="border border-border overflow-hidden rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Course</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Completed</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(c => (
              <tr key={c.id}>
                <td className="px-4 py-3 font-medium text-foreground">{c.courseName}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{c.employeeName}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm ${c.status === 'Completed' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {c.status === 'Completed' ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(c.completedDate)}</td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">No Attensi courses assigned yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Attensi Course' : 'Assign Attensi Course'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Course Name *</label>
              <Input value={form.courseName} onChange={e => setForm(f => ({ ...f, courseName: e.target.value }))} placeholder="e.g. Customer Service Fundamentals" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Employee *</label>
              <Select value={form.employeeId || 'none'} onValueChange={v => setForm(f => ({ ...f, employeeId: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {data?.employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Required">Required</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.status === 'Completed' && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Completed Date</label>
                  <Input type="date" value={form.completedDate} onChange={e => setForm(f => ({ ...f, completedDate: e.target.value }))} />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Attensi URL (optional)</label>
              <Input value={form.attensiUrl} onChange={e => setForm(f => ({ ...f, attensiUrl: e.target.value }))} placeholder="https://app.attensi.com/course/..." />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editId ? 'Update' : 'Assign Course'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
